/* tslint:disable:cyclomatic-complexity */
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../../classes/util';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { IMapcoreFeatureLayer, IMapcoreGraphicsLayer, IMapcoreMapImageLayer, isClassBreaksRenderer, isHeatmapRenderer, isTextSymbol, isUniqueValueRenderer, MapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

let Graphic: __esri.GraphicConstructor;
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-legend-symbol-ng',
    styleUrls: ['../../sharedStyles/base.scss', './legend-symbol.component.scss'],
    templateUrl: './legend-symbol.component.html'
})
export class LegendSymbolComponent extends BaseComponent implements OnInit, OnDestroy {
    get includeGoogleAnalytics() { return false; }

    private _uniqueLayerId: string;
    get uniqueLayerId() {
        return this._uniqueLayerId;
    }
    @Input() set uniqueLayerId(val: string) {
        this._uniqueLayerId = val;
        this.esriLayer = this.layerService.getEsriLayerByUniqueId(this.uniqueLayerId);
        this.refreshPreview();
    }

    @Input() @InputConverter() showAllSymbols: boolean = false;

    @Output() public readonly onsymbolchange = new EventEmitter<{ symbolIsColorRamp: boolean; uniqueLayerId: string }>();

    @ViewChild('legendSymbol') private readonly legendSymbol: ElementRef<HTMLDivElement>;

    esriLayer: MapCoreLayer;
    creatingColorRampSymbol = false;
    symbolIsColorRamp = false;

    constructor(_vcr: ViewContainerRef, private readonly layerService: LayerService) {
        super(_vcr, true);
        this.registerEvent('onsymbolchange', this.onsymbolchange);
        this.refreshPreview = util.debounce(this.refreshPreviewWork.bind(this), 50);
    }

    async ngOnInit() {
        [Graphic] = await Promise.all([ESRI.Graphic]);
        window.addEventListener('symbolChange', this.onWindowSymbolChange);
    }

    async ngOnDestroy() {
        window.removeEventListener('symbolChange', this.onWindowSymbolChange);
    }

    /**
     * Handles the global symbolChange event which is fired when the
     * symbol-editor component changes the symbol for a layer.
     */
    private readonly onWindowSymbolChange = (event: CustomEvent<string>) => {
        if (event.detail === this.uniqueLayerId) {
            this.refreshPreview();
            this.layerService.layersRefreshed.emit();
        }
    }

    refreshPreview;
    async refreshPreviewWork() {
        const symbolUtil = await ESRI.SymbolUtils;
        const esriLayer = this.esriLayer as IMapcoreGraphicsLayer | IMapcoreMapImageLayer | IMapcoreFeatureLayer;
        if (esriLayer) {
            if (!esriLayer.loaded) {
                await esriLayer.when();
            }
            let graphicsSymbol: __esri.Symbol = this.setupGraphicsSymbol(esriLayer);
            const renderers: __esri.Renderer[] = await this.setupRenderers(esriLayer);
            if (this.legendSymbol.nativeElement.childNodes.length) {
                for (const child of Array.from(this.legendSymbol.nativeElement.childNodes)) {
                    this.legendSymbol.nativeElement.removeChild(child);
                }
            }
            if (renderers.length) {
                for (const renderer of renderers) {
                    if ((renderer as any).symbol && (renderer as any).symbol.color && (renderer as any).symbol.color.a) {
                        (renderer as any).symbol.color.a *= esriLayer.opacity;
                    }
                    if ((renderer as any).symbol && (renderer as any).symbol.outline) {
                        (renderer as any).symbol.outline.color.a *= esriLayer.opacity;
                    }
                    if (isClassBreaksRenderer(renderer) || isUniqueValueRenderer(renderer)) {
                        const [colors, symbols] = this.getColorsOrSymbols(renderer);
                        if (colors.length) {
                            if (colors.length === 1) {
                                await this.renderSymbolPreview(isClassBreaksRenderer(renderer) ? renderer.classBreakInfos[0].symbol : renderer.uniqueValueInfos[0].symbol);
                            } else {
                                const colorRamp = await symbolUtil.renderColorRampPreviewHTML(colors, { gradient: renderer.type === 'class-breaks' ? true : false, height: 24, width: 24 });
                                if (this.legendSymbol.nativeElement.childNodes.length < 1) {
                                    this.legendSymbol.nativeElement.appendChild(colorRamp);
                                }
                                this.symbolIsColorRamp = true;
                            }
                        } else if (symbols.length) {
                            if (symbols.length === 1) {
                                await this.renderSymbolPreview(symbols[0]);
                            } else {
                                this.symbolIsColorRamp = false;
                                for (const symbol of symbols) {
                                    const previewHTML = await symbolUtil.renderPreviewHTML(symbol, { maxSize: 14, scale: true, size: { width: 29 } });
                                    this.legendSymbol.nativeElement.appendChild(previewHTML);
                                }
                            }
                        }
                        break;
                    } else if (isHeatmapRenderer(renderer)) {
                        const heatmapColors = renderer.colorStops.map(x => x.color).slice(1);
                        const heatmapColorRamp = await symbolUtil.renderColorRampPreviewHTML(heatmapColors, { align: 'horizontal', gradient: true, height: 24, width: 24 });
                        if (this.legendSymbol.nativeElement.childNodes.length < 1) {
                            this.legendSymbol.nativeElement.appendChild(heatmapColorRamp);
                        }
                        this.symbolIsColorRamp = true;
                        break;
                    } else {
                        await this.renderSymbolPreview((renderer as any).symbol);
                    }
                }
            } else if (graphicsSymbol) {
                if (isTextSymbol(graphicsSymbol)) {
                    graphicsSymbol = graphicsSymbol.clone();
                    (<__esri.TextSymbol>graphicsSymbol).text = 'Text';
                }
                if ((graphicsSymbol as any).color && (graphicsSymbol as any).color.a) {
                    graphicsSymbol.color.a *= esriLayer.opacity;
                }
                if ((graphicsSymbol as any).outline) {
                    (graphicsSymbol as any).outline.color.a *= esriLayer.opacity;
                }
                this.symbolIsColorRamp = false;
                try {
                    await symbolUtil.renderPreviewHTML(graphicsSymbol, { maxSize: 12, scale: true, node: this.legendSymbol.nativeElement, size: { width: 29 } });
                }
                catch (er) { }
            }
        }
    }

    private async renderSymbolPreview(symbol: __esri.Symbol) {
        const symbolUtil = await ESRI.SymbolUtils;
        this.symbolIsColorRamp = false;
        if (symbol.type === 'picture-marker') {
            this.layerService.fixPictureSymbols([symbol]);
        }
        await symbolUtil.renderPreviewHTML(symbol, { maxSize: 14, scale: true, node: this.legendSymbol.nativeElement, size: { width: 29 } });
    }

    private getColorsOrSymbols(renderer: __esri.Renderer) {
        const colors = [];
        const symbols = [];
        if (isClassBreaksRenderer(renderer)) {
            for (const classBreak of renderer.classBreakInfos) {
                colors.push(classBreak.symbol.color);
            }
        } else if (isUniqueValueRenderer(renderer)) {
            const max = renderer.defaultSymbol ? 1 : 2;
            if (renderer.defaultSymbol) {
                if (renderer.defaultSymbol.color) {
                    colors.push(renderer.defaultSymbol.color);
                } else {
                    symbols.push(renderer.defaultSymbol);
                }
            }
            for (const classBreak of renderer.uniqueValueInfos) {
                if (classBreak.symbol) {
                    if (classBreak.symbol.color) {
                        colors.push(classBreak.symbol.color);
                    } else {
                        symbols.push(classBreak.symbol);
                        if (!this.showAllSymbols && symbols.length >= max) {
                            break;
                        }
                    }
                }
            }
        }
        return [colors.filter(c => c), symbols];
    }

    setupRenderers(esriLayer: IMapcoreGraphicsLayer | IMapcoreMapImageLayer | IMapcoreFeatureLayer) {
        const renderers: __esri.Renderer[] = [];
        if (esriLayer.type === 'map-image') {
            for (const sublayer of esriLayer.sublayers.toArray()) {
                if (sublayer.renderer) {
                    renderers.push(util.deepClone(sublayer.renderer));
                }
            }
        } else if (esriLayer.type !== 'graphics' && esriLayer.renderer) {
            renderers.push(util.deepClone(esriLayer.renderer));
        }
        return renderers;
    }

    setupGraphicsSymbol(esriLayer: IMapcoreGraphicsLayer | IMapcoreMapImageLayer | IMapcoreFeatureLayer) {
        let graphicsSymbol: __esri.Symbol;
        if (esriLayer.type === 'graphics') {
            graphicsSymbol = util.deepClone(this.getSymbolForGraphicsLayer(esriLayer));
        }
        return graphicsSymbol;
    }

    // Note: The ESRI watch does not seem to be working below. The watch only triggers on certain object properties.
    // Assume ESRI will fix in the future.
    setRendererWatch() {
        let watchValues: string = null;
        if (this.esriLayer) {
            if (this.esriLayer.type === 'map-image') {
                for (const sublayer of this.esriLayer.sublayers) {
                    sublayer.watch('renderer, renderer.type, renderer.symbol, renderer.symbol.color, renderer.symbol.outline, renderer.symbol.outline.color, renderer.uniqueValueInfos', () => {
                        this.refreshPreview();
                    });
                }
            } else if (this.esriLayer.type === 'graphics') {
                const symbol = this.getSymbolForGraphicsLayer(this.esriLayer);
                if (symbol) {
                    watchValues = 'graphics';
                }
            } else {
                if ('renderer' in this.esriLayer) {
                    if (this.esriLayer.renderer && (this.esriLayer.renderer as any).symbol) {
                        watchValues = 'renderer, renderer.type, renderer.symbol, renderer.symbol.color, renderer.symbol.outline, renderer.symbol.outline.color';
                    } else if (this.esriLayer.renderer && this.esriLayer.renderer.type === 'heatmap') {
                        watchValues = 'renderer, renderer.type, renderer.blurRadius, renderer.colorStops, renderer.colorStops[1].color, renderer.colorStops[2].color';
                    }
                }
            }
            if (watchValues) {
                this.esriLayer.watch(watchValues, () => {
                    this.refreshPreview();
                });
            }
        }
    }

    changeSymbol() {
        if (this.symbolIsColorRamp) {
            this.onsymbolchange.emit({ symbolIsColorRamp: true, uniqueLayerId: this.uniqueLayerId });
        } else {
            this.onsymbolchange.emit({ symbolIsColorRamp: false, uniqueLayerId: this.uniqueLayerId });
        }
    }

    whichChild(target) {
        let element = target;
        let divParent = null;
        while (divParent === null) {
            if (element.tagName === 'DIV') {
                divParent = element;
            } else {
                element = element.parentElement;
            }
        }
        let i = 0;
        while (element.previousSibling !== null) {
            element = element.previousSibling;
            i += 1;
        }
        return i;
    }

    getSymbolForGraphicsLayer(esriLayer: IMapcoreGraphicsLayer) {
        if (esriLayer.mapcoreProps && esriLayer.mapcoreProps.symbol) {
            const graphic = new Graphic({ symbol: JSON.parse(esriLayer.mapcoreProps.symbol) });
            return graphic.symbol;
        } else {
            return this.getSymbolFromGraphics(esriLayer.graphics);
        }
    }

    getSymbolFromGraphics(graphics: __esri.Collection<__esri.Graphic>): __esri.Symbol {
        let retVal: __esri.Symbol = null;
        if (graphics && graphics.length) {
            retVal = graphics.getItemAt(graphics.length - 1).symbol;
        }
        return retVal;
    }
}
